import React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from './ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Skeleton } from './ui/skeleton';
import { ErrorState } from './ErrorState';
import { ListContinuation } from './ListContinuation';
import type { InfiniteList } from '../hooks/useInfiniteList';
import { cn } from '../lib/utils';

export interface ComboboxLeadingItem {
  value: string;
  label: string;
  /** Trigger text while this item is selected, when it differs from the option ('Organization: All'). */
  triggerLabel?: string;
}

export interface InfiniteComboboxProps<T> {
  /** A useInfiniteList the parent enables only while `open` — options are fresh on every open. */
  list: InfiniteList<T>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rowKey: (row: T) => string;
  /** Plain-text label for an option (and the trigger once selected). */
  optionLabel: (row: T) => string;
  /** Optional richer option body; defaults to optionLabel. */
  renderOption?: (row: T) => React.ReactNode;
  /** The selected value ('' for the leading item / nothing). */
  value: string;
  onValueChange: (value: string, row: T | null) => void;
  /** Items before the list ('All organizations', 'None'). */
  leadingItems?: ComboboxLeadingItem[];
  /**
   * The selected row when it may sit outside the loaded batches — fetched by
   * id by the caller and pinned first, so a selection never reads as missing.
   */
  selectedRow?: T | null;
  /** Trigger text when nothing (or a leading item) is selected. */
  placeholder: string;
  'aria-label'?: string;
  searchPlaceholder: string;
  /** Plural noun: 'organizations', 'event types'. */
  itemNoun: string;
  /** Title of the inline error when the first batch fails. */
  errorTitle: string;
  id?: string;
  disabled?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

/**
 * A server-searched single-choice picker over an unbounded set (DESIGN_SYSTEM
 * §5 Lists, pickers). cmdk runs with `shouldFilter={false}`: the search box
 * drives the list's server search, the loaded batches are the options, and a
 * 'Load more {itemNoun}' item is the keyboard fallback for the scroll sentinel.
 */
export function InfiniteCombobox<T>({
  list,
  open,
  onOpenChange,
  rowKey,
  optionLabel,
  renderOption,
  value,
  onValueChange,
  leadingItems = [],
  selectedRow,
  placeholder,
  'aria-label': ariaLabel,
  searchPlaceholder,
  itemNoun,
  errorTitle,
  id,
  disabled,
  triggerClassName,
  contentClassName,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-required': ariaRequired,
}: InfiniteComboboxProps<T>) {
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const leading = leadingItems.find((item) => item.value === value);
  const loadedSelected = list.items.find((row) => rowKey(row) === value);
  const selected = loadedSelected ?? (selectedRow && rowKey(selectedRow) === value ? selectedRow : null);
  const triggerText = (leading ? leading.triggerLabel ?? leading.label : undefined) ?? (selected ? optionLabel(selected) : placeholder);

  const pinned = selected && !loadedSelected && !list.search ? [selected] : [];
  const options = [...pinned, ...list.items];

  const choose = (next: string, row: T | null) => {
    onValueChange(next, row);
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          aria-required={ariaRequired}
          disabled={disabled}
          className={cn('h-9 justify-between font-normal', triggerClassName)}
        >
          <span className="truncate">{triggerText}</span>
          <ChevronsUpDown aria-hidden="true" className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[--radix-popover-trigger-width] min-w-64 p-0', contentClassName)} align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={list.searchInput}
            onValueChange={list.setSearchInput}
          />
          <CommandList ref={listRef}>
            {list.error != null && list.status !== 'appendError' ? (
              <div className="p-2">
                <ErrorState
                  compact
                  title={errorTitle}
                  error={list.error}
                  onRetry={list.reset}
                  isRetrying={list.status === 'initial' || list.status === 'refreshing'}
                />
              </div>
            ) : list.status === 'initial' ? (
              <div className="space-y-2 p-2" aria-busy="true">
                <Skeleton className="h-7 w-full" />
                <Skeleton className="h-7 w-full" />
                <Skeleton className="h-7 w-3/4" />
              </div>
            ) : (
              <>
                {options.length === 0 ? (
                  <CommandEmpty>
                    {list.search ? `No ${itemNoun} match "${list.search}".` : `No ${itemNoun} yet.`}
                  </CommandEmpty>
                ) : null}
                <CommandGroup className={cn(list.status === 'refreshing' && 'opacity-50')}>
                  {!list.search
                    ? leadingItems.map((item) => (
                        <CommandItem key={`lead-${item.value}`} value={`lead-${item.value}`} onSelect={() => choose(item.value, null)}>
                          <Check aria-hidden="true" className={cn(value === item.value ? 'opacity-100' : 'opacity-0')} />
                          {item.label}
                        </CommandItem>
                      ))
                    : null}
                  {options.map((row) => {
                    const key = rowKey(row);
                    return (
                      <CommandItem key={key} value={key} onSelect={() => choose(key, row)}>
                        <Check aria-hidden="true" className={cn(value === key ? 'opacity-100' : 'opacity-0')} />
                        <span className="min-w-0 flex-1">{renderOption ? renderOption(row) : optionLabel(row)}</span>
                      </CommandItem>
                    );
                  })}
                  <ListContinuation
                    variant="command"
                    status={list.status}
                    hasMore={list.hasMore}
                    onLoadMore={list.loadMore}
                    onRetryBatch={list.retryBatch}
                    onReset={list.reset}
                    error={list.error}
                    itemNoun={itemNoun}
                    loadedCount={list.items.length}
                    totalCount={list.totalCount}
                    rootRef={listRef}
                  />
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
